import { useArchiveGoal, useDuplicateGoal } from 'features/goals';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router';
import {
  ArchiveDocumentsBoxBig,
  CopyDuplicateObject,
  Trash16,
  Upload,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenCreateGoalTemplateDialog } from 'shared/contexts/create-goal-template';
import { useOpenDeleteGoalDialog } from 'shared/contexts/delete-goal';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes';
import { Goal } from 'shared/types/goal';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { ContextMenuOptions } from 'shared/views/goal-filter-list/types';

export type ManageListProps = {
  goal: Goal;
  onClose: () => void;
  excludedOptions?: ContextMenuOptions[];
};

export const ManageList: React.FC<ManageListProps> = ({
  goal,
  onClose,
  excludedOptions,
}) => {
  const { t } = useTranslation();
  const user = useUser();
  const isOnDetailView = useMatch(Paths.GoalDetail);

  const openTemplateDialog = useOpenCreateGoalTemplateDialog();
  const openDeleteGoalDialog = useOpenDeleteGoalDialog();
  const archiveGoal = useArchiveGoal();
  const navigate = useNavigate();
  const duplicateGoal = useDuplicateGoal();

  const onDuplicateGoal = async (e: React.MouseEvent) => {
    e.preventDefault();
    const newGoalId = await duplicateGoal(goal);

    if (newGoalId) {
      navigate(replaceUrlParams(Paths.GoalDetail, { id: newGoalId }));
    }

    onClose();
  };

  const onCreateTemplate = (e: React.MouseEvent) => {
    e.preventDefault();
    openTemplateDialog(goal.id);
    onClose();
  };

  const onDeleteGoal = (e: React.MouseEvent) => {
    e.preventDefault();
    openDeleteGoalDialog(
      goal,
      isOnDetailView?.params.id === goal.id ? Paths.Goals : undefined,
    );
    onClose();
  };

  const onArchiveGoal = (e: React.MouseEvent) => {
    e.preventDefault();
    archiveGoal(goal);
  };

  return (
    <PopupMenuList>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onDuplicateGoal}
          start={<Icon icon={CopyDuplicateObject} />}
        >
          {t('context-menus.goal.labels.duplicate-goal')}
        </PopupMenuButton>
      </PopupMenuListItem>
      {user?.email?.endsWith('@griply.app') && !goal.parentIds?.length && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onCreateTemplate}
            startColor={Colors.Info}
            start={<Icon icon={Upload} />}
            textColor={Colors.Info}
          >
            {t('context-menus.goal.labels.create-template')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
      {!excludedOptions?.includes(ContextMenuOptions.Archive) && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onArchiveGoal}
            start={<Icon icon={ArchiveDocumentsBoxBig} />}
          >
            {t(
              !!goal.archivedAt
                ? 'context-menus.goal.labels.de-archive'
                : 'context-menus.goal.labels.archive',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
      {!excludedOptions?.includes(ContextMenuOptions.Delete) && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDeleteGoal}
            start={<Icon icon={Trash16} />}
            startColor={Colors.Error}
            textColor={Colors.Error}
          >
            {t('context-menus.goal.labels.delete')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}
    </PopupMenuList>
  );
};
